"use client";

import { useState } from 'react';
import { ShareIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import Toast from './Toast';

export type EntityShareButtonProps = {
  label: string;
  title: string;
  text: string;
  url: string;
  className?: string;
};

/** Native sharing where available, with a useful canonical-link copy fallback. */
export function EntityShareButton({ label, title, text, url, className = '' }: EntityShareButtonProps) {
  const [notice, setNotice] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  async function share() {
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title, text, url });
        setNotice({ message: `${title} shared.`, type: 'success' });
        return;
      }
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setNotice({ message: 'Canonical link copied.', type: 'success' });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setNotice({ message: 'Sharing is unavailable. Copy the page URL from your browser.', type: 'error' });
    }
  }

  return (
    <>
      <Button variant="secondary" className={className} onClick={() => void share()}>
        <ShareIcon className="h-4 w-4" aria-hidden="true" />
        {label}
      </Button>
      <Toast
        isVisible={notice !== null}
        message={notice?.message ?? ''}
        type={notice?.type ?? 'success'}
        onClose={() => setNotice(null)}
      />
    </>
  );
}
